/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description Formily-compatible button with dropdown to add optional patient fields
 */
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { Button, Dropdown, MenuProps } from 'antd';
import { Plus } from '@icon-park/react';
import { observer, useForm } from '@formily/react';

import { OPTIONAL_PATIENT_FIELDS_CONFIG } from 'app-constants/eligibility';

const getOptionalPatientFieldKeys = (fields: typeof OPTIONAL_PATIENT_FIELDS_CONFIG) => _.map(fields, 'key');

const AddOptionalFieldButton: React.FC<EligibilityBatchUpload.IAddOptionalFieldButtonProps> = ({ onFieldAdd }) => {
	const form = useForm();
	const [open, setOpen] = useState(false);

	const getAvailableFields = useCallback(
		() =>
			_.filter(OPTIONAL_PATIENT_FIELDS_CONFIG, (field) => {
				const display = form.getFieldState(field.key, (fieldState) => fieldState?.display);
				return display !== 'visible';
			}),
		[form],
	);

	const [availableFields, setAvailableFields] = useState(getAvailableFields);

	const refreshAvailableFields = useCallback(() => {
		const nextAvailableFields = getAvailableFields();
		setAvailableFields((currentAvailableFields) => {
			const currentFieldKeys = getOptionalPatientFieldKeys(currentAvailableFields);
			const nextFieldKeys = getOptionalPatientFieldKeys(nextAvailableFields);
			return _.isEqual(currentFieldKeys, nextFieldKeys) ? currentAvailableFields : nextAvailableFields;
		});
	}, [getAvailableFields]);

	useEffect(() => {
		const subscriberId = form.subscribe(refreshAvailableFields);
		refreshAvailableFields();
		return () => {
			form.unsubscribe(subscriberId);
		};
	}, [form, refreshAvailableFields]);

	const menuItems: MenuProps['items'] = _.map(availableFields, (field) => ({
		key: field.key,
		label: field.label,
	}));

	const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
		const fieldKey = key as EligibilityBatchUpload.IOptionalPatientFieldKey;
		form.setValuesIn(fieldKey, undefined);
		form.setFieldState(fieldKey, { display: 'visible' });
		onFieldAdd?.(fieldKey);
		refreshAvailableFields();
		setOpen(false);
	};

	const handleOpenChange = (nextOpen: boolean) => {
		if (nextOpen) {
			refreshAvailableFields();
		}
		setOpen(nextOpen);
	};

	if (_.isEmpty(availableFields)) {
		return null;
	}

	return (
		<Dropdown menu={{ items: menuItems, onClick: handleMenuClick }} trigger={['click']} open={open} onOpenChange={handleOpenChange}>
			<Button type='link' icon={<Plus />} data-testid='add-optional-patient-field-button' style={{ padding: '4px 0' }}>
				{'Add Additional Patient Field'}
			</Button>
		</Dropdown>
	);
};

export default observer(AddOptionalFieldButton);
